import {TopBar as TopBarUI} from '@shopify/online-store-ui';
import {useMediaLayout, useToggle} from '@shopify/react-hooks';
import {useLocation, useNavigate} from 'react-router-dom';
import {useI18n} from '@shopify/react-i18n';
import {Button, ActionList, Popover, BlockStack} from '@shopify/polaris';
import {MenuHorizontalIcon} from '@shopify/polaris-icons';
import {usePreview} from 'utilities/use-preview';
import {isShopifyMobile} from '@shopify/app-bridge/utilities';
import {
  useSearch,
  useTranslatableResourceForm,
} from 'features/Translations/hooks';
import {redirectToAdminUrl} from 'utilities/redirect';

import {SearchInput} from '../SearchInput';

import {ResourceTypeSelector, ContextSelector} from './components';

export interface Props {
  loading: boolean;
  exitAction: () => void;
  sidebarHasNoResults: boolean | null;
  hideMarketSelector: boolean;
}

export const Group = TopBarUI.LayoutGroup;

export function TopBar({
  exitAction,
  sidebarHasNoResults,
  loading,
  hideMarketSelector,
}: Props) {
  const [i18n] = useI18n();
  const navigate = useNavigate();
  const {
    loading: previewLoading,
    canPreview,
    preview,
    previewForm,
  } = usePreview();
  const {save, dirty, submitting} = useTranslatableResourceForm();
  const {isActive: searchIsActive} = useSearch();
  const mobile = useMediaLayout('(max-width: 48rem)');
  const location = useLocation();

  const {value: showPreviewPopover, toggle: togglePreviewPopover} =
    useToggle(false);

  const popoverActivator = (
    <BlockStack align="center">
      <Button
        icon={MenuHorizontalIcon}
        size="slim"
        variant="plain"
        textAlign="center"
        onClick={togglePreviewPopover}
        accessibilityLabel="More actions"
      />
    </BlockStack>
  );

  const previewButton = mobile ? (
    <Popover
      active={showPreviewPopover}
      activator={popoverActivator}
      onClose={togglePreviewPopover}
    >
      <ActionList
        actionRole="menuitem"
        items={[
          {
            content: i18n.translate('Common.viewStore'),
            onAction: preview,
            disabled: previewLoading || !canPreview,
          },
        ]}
      />
    </Popover>
  ) : (
    <Button
      disabled={previewLoading || !canPreview}
      onClick={preview}
      size="large"
    >
      {i18n.translate('Common.viewStore')}
    </Button>
  );

  const saveButton = (
    <Button
      variant="primary"
      loading={submitting}
      disabled={!dirty}
      onClick={save}
      size={mobile ? 'slim' : 'large'}
    >
      {i18n.translate('Common.save')}
    </Button>
  );

  const onExit = () => {
    exitAction();
    // when location.key is default it means there is no history in the stack
    // https://github.com/remix-run/react-router/discussions/9788#discussioncomment-4604278
    if (location.key === 'default') {
      redirectToAdminUrl({
        adminUrl: '/',
        newContext: false,
      });
    } else {
      navigate(-1);
    }
  };

  const searchInputMarkup = mobile ? null : <SearchInput disabled={loading} />;

  const contextSelectorMarkup = (
    <ContextSelector
      disabled={loading && sidebarHasNoResults !== true}
      hideMarketSelector={hideMarketSelector}
    />
  );

  const resourceTypeSelectorMarkup = (
    <ResourceTypeSelector disabled={loading && sidebarHasNoResults !== true} />
  );

  const mobileTopBar = (
    <>
      {previewForm}
      <TopBarUI>
        <TopBarUI.LayoutGroup
          padded="right"
          grow
          spacing="tight"
          distributeSpace
        >
          <TopBarUI.LayoutGroup.Item>
            <TopBarUI.ExitAction
              accessibilityLabel={i18n.translate('Topbar.exit')}
              label={i18n.translate('Topbar.exit')}
              onAction={onExit}
            />
          </TopBarUI.LayoutGroup.Item>

          <TopBarUI.LayoutGroup.Item>
            {contextSelectorMarkup}
          </TopBarUI.LayoutGroup.Item>

          <TopBarUI.LayoutGroup.Item grow>
            {resourceTypeSelectorMarkup}
          </TopBarUI.LayoutGroup.Item>

          <TopBarUI.LayoutGroup.Item>{previewButton}</TopBarUI.LayoutGroup.Item>

          {!isShopifyMobile() && saveButton}
        </TopBarUI.LayoutGroup>
      </TopBarUI>
    </>
  );

  if (mobile) return mobileTopBar;

  return (
    <>
      {previewForm}
      <TopBarUI>
        <TopBarUI.LayoutGroup grow spacing="base-tight" distributeSpace>
          <TopBarUI.ExitAction
            accessibilityLabel={i18n.translate('Topbar.exit')}
            label={i18n.translate('Topbar.exit')}
            onAction={onExit}
            animated
          >
            {i18n.translate('Common.appName')}
          </TopBarUI.ExitAction>

          <TopBarUI.LayoutGroup center grow shrink padded spacing="base-tight">
            <TopBarUI.LayoutGroup.Item grow={searchIsActive}>
              {searchInputMarkup}
            </TopBarUI.LayoutGroup.Item>

            {searchIsActive ? null : (
              <TopBarUI.LayoutGroup.Item>
                {contextSelectorMarkup}
              </TopBarUI.LayoutGroup.Item>
            )}

            {searchIsActive ? null : (
              <TopBarUI.LayoutGroup.Item>
                {resourceTypeSelectorMarkup}
              </TopBarUI.LayoutGroup.Item>
            )}
          </TopBarUI.LayoutGroup>

          <TopBarUI.LayoutGroup alignRight padded="right" spacing="base-tight">
            {previewButton}
            {!isShopifyMobile() && saveButton}
          </TopBarUI.LayoutGroup>
        </TopBarUI.LayoutGroup>
      </TopBarUI>
    </>
  );
}
